import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { Shield, Eye, EyeOff } from 'lucide-react';

export default function ManagerLogin() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password.trim()) {
      setError('Please fill in all fields.');
      return;
    }
    setLoading(true);
    setError('');

    try {
      const { data, error: authErr } = await supabase.auth.signInWithPassword({
        email: email.trim(),
        password: password.trim(),
      });

      if (authErr) {
        setError('Invalid email or password.');
        setLoading(false);
        return;
      }

      // Verify this user is actually a manager
      const { data: mgr } = await supabase
        .from('managers')
        .select('id, name, tier')
        .eq('auth_user_id', data.user.id)
        .maybeSingle();

      if (!mgr) {
        await supabase.auth.signOut();
        setError('Access denied. This account is not a manager account.');
        setLoading(false);
        return;
      }

      navigate('/manager/dashboard');
    } catch (err) {
      setError('Something went wrong. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0F172A 0%, #1E1B4B 50%, #0F172A 100%)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 20,
    }}>
      <div style={{ width: '100%', maxWidth: 420 }}>

        {/* Logo + header */}
        <div style={{ textAlign: 'center', marginBottom: 36 }}>
          <div style={{
            width: 64, height: 64, borderRadius: '50%',
            background: 'linear-gradient(135deg,#4F8EF7,#7C3AED)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 16px',
            boxShadow: '0 8px 32px rgba(79,142,247,0.4)',
          }}>
            <Shield size={28} color="#fff" />
          </div>
          <h1 style={{
            fontSize: 26, fontWeight: 800, color: '#F1F5F9',
            margin: '0 0 6px',
          }}>
            Manager Portal
          </h1>
          <p style={{ color: '#64748B', fontSize: 14, margin: 0 }}>
            UNIVERSE Support Management System
          </p>
        </div>

        {/* Card */}
        <div style={{
          background: 'rgba(255,255,255,0.04)',
          border: '1px solid rgba(255,255,255,0.08)',
          borderRadius: 20,
          padding: 32,
          backdropFilter: 'blur(10px)',
        }}>
          <form onSubmit={handleLogin}>
            {/* Email */}
            <div style={{ marginBottom: 16 }}>
              <label style={{
                display: 'block', fontSize: 12, fontWeight: 700,
                color: '#94A3B8', marginBottom: 8, letterSpacing: 0.5,
              }}>
                EMAIL ADDRESS
              </label>
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="manager@inncatechnologies.com"
                autoComplete="email"
                style={{
                  width: '100%', padding: '12px 14px',
                  background: 'rgba(255,255,255,0.06)',
                  border: '1.5px solid rgba(255,255,255,0.1)',
                  borderRadius: 12, fontSize: 14, color: '#F1F5F9',
                  outline: 'none',
                }}
                onFocus={e => e.target.style.borderColor = '#4F8EF7'}
                onBlur={e => e.target.style.borderColor = 'rgba(255,255,255,0.1)'}
              />
            </div>

            {/* Password */}
            <div style={{ marginBottom: 20 }}>
              <label style={{
                display: 'block', fontSize: 12, fontWeight: 700,
                color: '#94A3B8', marginBottom: 8, letterSpacing: 0.5,
              }}>
                PASSWORD
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showPass ? 'text' : 'password'}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="••••••••"
                  autoComplete="current-password"
                  style={{
                    width: '100%', padding: '12px 44px 12px 14px',
                    background: 'rgba(255,255,255,0.06)',
                    border: '1.5px solid rgba(255,255,255,0.1)',
                    borderRadius: 12, fontSize: 14, color: '#F1F5F9',
                    outline: 'none',
                  }}
                  onFocus={e => e.target.style.borderColor = '#4F8EF7'}
                  onBlur={e => e.target.style.borderColor = 'rgba(255,255,255,0.1)'}
                />
                <button
                  type="button"
                  onClick={() => setShowPass(!showPass)}
                  style={{
                    position: 'absolute', right: 12, top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none', border: 'none',
                    cursor: 'pointer', color: '#64748B', padding: 4,
                  }}>
                  {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            {/* Error */}
            {error && (
              <div style={{
                background: 'rgba(239,68,68,0.1)',
                border: '1px solid rgba(239,68,68,0.3)',
                borderRadius: 10, padding: '10px 14px',
                marginBottom: 16, fontSize: 13, color: '#FCA5A5',
              }}>
                {error}
              </div>
            )}

            {/* Submit */}
            <button
              type="submit"
              disabled={loading}
              style={{
                width: '100%', padding: '13px 0',
                background: loading
                  ? 'rgba(79,142,247,0.5)'
                  : 'linear-gradient(135deg,#4F8EF7,#7C3AED)',
                border: 'none', borderRadius: 12,
                color: '#fff', fontWeight: 700, fontSize: 15,
                cursor: loading ? 'not-allowed' : 'pointer',
                boxShadow: loading ? 'none' : '0 4px 20px rgba(79,142,247,0.35)',
                transition: 'all 0.2s',
              }}>
              {loading ? 'Signing in...' : 'Sign In to Manager Portal'}
            </button>
          </form>

          {/* Divider */}
          <div style={{
            textAlign: 'center', marginTop: 24,
            fontSize: 12, color: '#475569',
          }}>
            This portal is for authorized managers only.
            <br />
            Contact admin@inncatechnologies.com for access.
          </div>
        </div>

        {/* Footer */}
        <div style={{
          textAlign: 'center', marginTop: 20,
          fontSize: 11, color: '#334155',
        }}>
          UNIVERSE Careers · Manager Portal · Confidential
        </div>
      </div>
    </div>
  );
}